import { gql, useQuery } from '@apollo/client'

import { useGetNewestData } from '@/clients/utoniq-core'
import {
  CurrentUserFragmentDoc,
  FetchNewGlobalCollectionsQuery,
  FetchNewGlobalCollectionsQueryVariables,
  GlobalCollectionsQuery,
  GlobalCollectionsQueryVariables,
  UserCollectionBoxFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useCurrentUser } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { makeViewModel } from '@/core/architecture'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { userClaims } = useCurrentUser()
  const { analytics } = useAnalytics()

  const { isFirstLoadRef } = useGetNewestData<
    FetchNewGlobalCollectionsQuery,
    FetchNewGlobalCollectionsQueryVariables
  >({
    query: FETCH_NEW_QUERY,
    variables: { pageInput: { limit: 20 } },
    fields: [
      {
        fieldName: 'ownedUserCollections',
        fragment: UserCollectionBoxFragmentDoc,
      },
    ],
  })

  const { data, loading, fetchMore } = useQuery<
    GlobalCollectionsQuery,
    GlobalCollectionsQueryVariables
  >(QUERY, {
    onCompleted({ ownedUserCollections }) {
      isFirstLoadRef.current = false
      if (!ownedUserCollections.edges.length) {
        analytics?.track('impression_noData', {
          applicationKey: config.applicationKey,
          name: 'globalCollections',
        })
      }
    },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  })
  const isNextFetch = !!data

  return {
    state: {
      loading: loading && !isNextFetch,
      hasNextPage: !!data?.ownedUserCollections?.pageInfo.hasNextPage,
      userCollections:
        data?.ownedUserCollections?.edges.filter(
          ({ node }) => node?.itemOwnedCount >= 1
        ) ?? null,
      user: data?.user ?? null,
      isArtist: !!userClaims?.orgs?.[config.organizationId]?.artist,
      followArtists: data?.user?.organizationFollowees ?? null,
      memberArtists: data?.user?.organizationPatrons ?? null,
    },
    actions: {
      async onFetchMore() {
        await fetchMore({
          variables: {
            pageInput: {
              cursor: data?.ownedUserCollections?.pageInfo.endCursor,
            },
          },
        })
      },
    },
  }
})

const QUERY = gql`
  query GlobalCollections($userId: String, $pageInput: PageInput) {
    ownedUserCollections(pageInput: $pageInput, userId: $userId) {
      edges {
        node {
          ...UserCollectionBox
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
    user {
      ...CurrentUser
    }
  }
  ${UserCollectionBoxFragmentDoc}
  ${CurrentUserFragmentDoc}
`

const FETCH_NEW_QUERY = gql`
  query FetchNewGlobalCollections($userId: String, $pageInput: PageInput) {
    ownedUserCollections(pageInput: $pageInput, userId: $userId) {
      edges {
        node {
          ...UserCollectionBox
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
  ${UserCollectionBoxFragmentDoc}
`
